import { ModelBase } from '../../core/model/model-base';
import { Account } from './account';
import { AccountFunction, AccountType } from './const/enum';
import { DimensionLabels } from './dimension-labels';

export class AccountPlan extends ModelBase implements DimensionLabels {
  constructor() {
    super();
    this._accounts = [];

    Object.defineProperties(this, {
      _accounts: { writable: true, enumerable: false },
    });
  }

  private _accounts: Account[];
  get accounts(): Account[] {
    return this._accounts;
  }
  set accounts(value: Account[]) {
    if (value instanceof Array) {
      value.forEach(item => {
        if (item instanceof Account) this._accounts.push(item);
        else this._accounts.push(Object.assign(new Account(), item));
      });
    }
  }

  bankgiroAccount: string;
  get bankgiroAccounts(): Account[] {
    return this.accounts.filter(x => x.type === AccountType.Asset).sort();
  }

  cashAccount: string;
  get cashAccounts(): Account[] {
    return this.accounts.filter(x => x.type === AccountType.Asset).sort();
  }

  get claimAccounts(): Account[] {
    return this.accounts.filter(x => x.function === AccountFunction.CustomerClaim).sort();
  }
  customerClaimAccount: string;
  inboundVatAccount: string;
  get inboundVatAccounts(): Account[] {
    return this.accounts
      .filter(x => x.function === AccountFunction.IngoingVat)
      .map(x => Object.assign(new Account(), x))
      .sort();
  }
  incomeAccount: string;
  get incomeAccounts(): Account[] {
    return this.accounts
      .filter(
        x =>
          x.type === AccountType.Income ||
          x.type === AccountType.OwnCapitalAndDebt ||
          x.type === AccountType.FinancialIncomeAndCost ||
          x.function === AccountFunction.CustomerClaim
      )
      .sort();
  }
  advancePaymentAccount: string;
  get advancePaymentAccounts(): Account[] {
    return this.accounts.filter(x => x.type === AccountType.OwnCapitalAndDebt).sort();
  }
  advancePaymentRentAccount: string;
  get advancePaymentRentAccounts(): Account[] {
    return this.accounts.filter(x => x.function === AccountFunction.AdvancePayment).sort();
  }
  incorrectPaymentBalanceAccount: string;
  get incorrectPaymentBalanceAccounts(): Account[] {
    return this.accounts.filter(x => x.type === AccountType.Asset || x.type === AccountType.OwnCapitalAndDebt).sort();
  }
  interestIncomeAccount: string;
  get interestIncomeAccounts(): Account[] {
    return this.accounts.filter(x => x.type === AccountType.FinancialIncomeAndCost).sort();
  }
  invoiceFeeAccount: string;
  name: string;
  outboundDormantVatAccount: string;

  enableCostPlace: boolean;
  enableRoundingCoding: boolean;
  enableCostBearer: boolean;
  enableProject: boolean;
  enableCounterPart: boolean;
  enableArticleCostPlace: boolean;
  enableFunction: boolean;
  costPlaceLabel: string;
  costBearerLabel: string;
  projectLabel: string;
  counterpartLabel: string;
  functionLabel: string;

  get outboundDormantVatAccounts(): Account[] {
    return this.accounts.filter(x => x.function === AccountFunction.OutboundVatPeriodization).sort();
  }
  roundingAccount: string;
  get vatAccounts(): Account[] {
    return this.accounts.filter(x => x.function === AccountFunction.OutgoingVat).sort();
  }
  vat12pctAccount: string;
  vat25pctAccount: string;
  vat6pctAccount: string;
  dwellingInspectionIncomeAccount: string;
  dwellingInspectionClaimAccount: string;
  imdIncomeAccount: string;
  customerLossAccount: string;

  timeReportBalanceAccount: string;

  turnoverCorrectionAccountId: string;
  turnoverCorrectionNoVatAccountId: string;
  turnoverCorrectionVat25AccountId: string;
  turnoverCorrectionVat12AccountId: string;
  turnoverCorrectionVat6AccountId: string;
  turnoverCorrectionVat25VoluntaryAccountId: string;
  turnoverCorrectionVat12VoluntaryAccountId: string;
  turnoverCorrectionVat6VoluntaryAccountId: string;

  get customerLossAccounts(): Account[] {
    return this.accounts
      .filter(
        x =>
          x.type === AccountType.OterExternalExpense2 ||
          x.type === AccountType.OterExternalExpense ||
          x.type === AccountType.Expense ||
          x.type === AccountType.Income
      )
      .sort();
  }

  toJSON() {
    return {
      accounts: this.accounts,
      bankgiroAccount: this.bankgiroAccount,
      cashAccount: this.cashAccount,
      customerClaimAccount: this.customerClaimAccount,
      id: this.id,
      name: this.name,
      advancePaymentAccount: this.advancePaymentAccount,
      advancePaymentRentAccount: this.advancePaymentRentAccount,
      inboundVatAccount: this.inboundVatAccount,
      incomeAccount: this.incomeAccount,
      incorrectPaymentBalanceAccount: this.incorrectPaymentBalanceAccount,
      interestIncomeAccount: this.interestIncomeAccount,
      invoiceFeeAccount: this.invoiceFeeAccount,
      outboundDormantVatAccount: this.outboundDormantVatAccount,
      roundingAccount: this.roundingAccount,
      vat12pctAccount: this.vat12pctAccount,
      vat25pctAccount: this.vat25pctAccount,
      vat6pctAccount: this.vat6pctAccount,
      dwellingInspectionIncomeAccount: this.dwellingInspectionIncomeAccount,
      dwellingInspectionClaimAccount: this.dwellingInspectionClaimAccount,
      imdIncomeAccount: this.imdIncomeAccount,
      customerLossAccount: this.customerLossAccount,
      timeReportBalanceAccount: this.timeReportBalanceAccount,
      turnoverCorrectionAccountId: this.turnoverCorrectionAccountId,
      turnoverCorrectionNoVatAccountId: this.turnoverCorrectionNoVatAccountId,
      turnoverCorrectionVat25AccountId: this.turnoverCorrectionVat25AccountId,
      turnoverCorrectionVat12AccountId: this.turnoverCorrectionVat12AccountId,
      turnoverCorrectionVat6AccountId: this.turnoverCorrectionVat6AccountId,
      turnoverCorrectionVat25VoluntaryAccountId: this.turnoverCorrectionVat25VoluntaryAccountId,
      turnoverCorrectionVat12VoluntaryAccountId: this.turnoverCorrectionVat12VoluntaryAccountId,
      turnoverCorrectionVat6VoluntaryAccountId: this.turnoverCorrectionVat6VoluntaryAccountId,
      enableCostBearer: this.enableCostBearer,
      enableCostPlace: this.enableCostPlace,
      enableProject: this.enableProject,
      enableRoundingCoding: this.enableRoundingCoding,
      enableCounterPart: this.enableCounterPart,
      enableArticleCostPlace: this.enableArticleCostPlace,
      enableFunction: this.enableFunction,
      costPlaceLabel: this.costPlaceLabel,
      costBearerLabel: this.costBearerLabel,
      projectLabel: this.projectLabel,
      counterpartLabel: this.counterpartLabel,
      functionLabel: this.functionLabel,
    };
  }

  toString(): string {
    return this.name;
  }
}
